import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Filter } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleCOMPASS } from "@/components/compass/ModuleTitleCOMPASS";

const CATEGORIES = [
  { key: "quality_of_work", label: "Quality of work" },
  { key: "reliability_conduct", label: "Reliability / conduct" },
  { key: "communication", label: "Communication" },
  { key: "policy_compliance", label: "Policy compliance" },
  { key: "other", label: "Other" },
];

export default function CompassCalibration() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [filters, setFilters] = useState({ campus_id: "", status: "", duration: "", category: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [d, c] = await Promise.all([
        api.get("/compass/calibration", { params }),
        api.get("/campuses"),
      ]);
      setData(d.data);
      setCampuses(c.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filters]);

  return (
    <div className="space-y-6" data-testid="compass-calibration">
      <Link to="/compass" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> COMPASS home
      </Link>
      <div>
        <ModuleTitleCOMPASS />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">Calibration Dashboard</h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          All plans across all campuses. Filter by campus, status, duration, or category
          to sanity-check that COMPASS is being used consistently and fairly.
        </p>
      </div>

      {/* Filters */}
      <div className="patria-card p-4 grid grid-cols-1 sm:grid-cols-5 gap-3">
        <select className="patria-input" value={filters.campus_id} onChange={(e) => setFilters({ ...filters, campus_id: e.target.value })} data-testid="cal-filter-campus">
          <option value="">All campuses</option>
          {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="patria-input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="cal-filter-status">
          <option value="">All statuses</option>
          {["draft", "awaiting_po", "approved", "delivered", "acknowledged", "in_progress", "paused", "completed"].map((s) =>
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          )}
        </select>
        <select className="patria-input" value={filters.duration} onChange={(e) => setFilters({ ...filters, duration: e.target.value })} data-testid="cal-filter-duration">
          <option value="">All durations</option>
          <option value="30">30 days</option>
          <option value="60">60 days</option>
          <option value="90">90 days</option>
        </select>
        <select className="patria-input" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} data-testid="cal-filter-category">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button className="patria-btn-secondary" onClick={() => setFilters({ campus_id: "", status: "", duration: "", category: "" })} data-testid="cal-clear-filters">
          <Filter className="h-4 w-4" strokeWidth={1.5} /> Clear filters
        </button>
      </div>

      {loading || !data ? (
        <p className="italic text-charcoal-muted">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total plans" value={data.total} />
            <Stat label="Missed check-ins" value={data.missed_checkin_flags} accent="terra" />
            <Stat label="30 · 60 · 90"
              value={`${data.by_duration[30] || 0} · ${data.by_duration[60] || 0} · ${data.by_duration[90] || 0}`}
            />
            <Stat label="Outcomes recorded" value={Object.values(data.by_outcome || {}).reduce((a, b) => a + b, 0)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <StatCard title="By campus" rows={data.by_campus.map((r) => [r.campus, r.count])} />
            <StatCard title="By status" rows={Object.entries(data.by_status).map(([k, v]) => [k.replace("_", " "), v])} />
            <StatCard title="By category" rows={Object.entries(data.by_category).map(([k, v]) => [k.replace("_", " "), v])} />
          </div>

          <div className="overflow-hidden rounded-2xl shadow-card">
            <table className="patria-table w-full">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Manager</th>
                  <th>Campus</th>
                  <th>Category</th>
                  <th>Timeline</th>
                  <th>Status</th>
                  <th>Missed</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {data.plans.map((p) => (
                  <tr key={p.id} data-testid={`cal-plan-${p.id}`} className="cursor-pointer hover:bg-cream-100"
                      onClick={() => navigate(`/compass/plans/${p.id}`)}>
                    <td className="font-medium text-forest-900">{p.employee_name}</td>
                    <td>{p.manager_name}</td>
                    <td>{p.campus_name}</td>
                    <td className="capitalize">{p.gap_category?.replace("_", " ")}</td>
                    <td>{p.timeline_days} days</td>
                    <td>
                      <span className={p.status === "paused" ? "patria-chip-terra" : "patria-chip-green"}>
                        {p.status.replace("_", " ")}
                      </span>
                    </td>
                    <td>
                      {p.missed_checkins > 0 ? (
                        <span className="patria-chip-terra">{p.missed_checkins}</span>
                      ) : (
                        <span className="text-charcoal-muted">—</span>
                      )}
                    </td>
                    <td className="capitalize">{p.outcome?.decision?.replace("_", " ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "green" }) {
  return (
    <div className="patria-card p-4">
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{label}</p>
      <p className={`mt-1 text-3xl font-serif ${accent === "terra" ? "text-terracotta-600" : "text-forest-900"}`}>{value}</p>
    </div>
  );
}

function StatCard({ title, rows }) {
  return (
    <div className="patria-card p-5">
      <h3 className="text-lg font-serif text-forest-900 mb-2">{title}</h3>
      <ul className="text-sm">
        {rows.length === 0 && <li className="italic text-charcoal-muted">No data.</li>}
        {rows.map(([k, v]) => (
          <li key={k} className="flex items-center justify-between py-1 border-b border-sage-300/40 last:border-b-0">
            <span className="capitalize">{k}</span>
            <span className="font-medium text-forest-900">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
